import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollableFormShell } from "@/modules/add-new-data/components/scrollable-form-shell";
import {
  formatOfficialAgeDisplay,
  getAgeSourceLabel,
  resolveMemberAge,
  type BirthdateResolverInput,
} from "@/modules/add-new-data/lib/age";
import { cleanAddNewDataValues } from "@/modules/add-new-data/lib/cleansing";
import { parseRbimDisplayCodes } from "@/modules/add-new-data/lib/rbim-display-codes";
import type { AddNewDataFormValues } from "@/modules/add-new-data/lib/schema";

type ReviewSubmitStepProps = {
  values: AddNewDataFormValues;
};

export function ReviewSubmitStep({ values }: ReviewSubmitStepProps) {
  const rbimDisplayCodes = parseRbimDisplayCodes(values.household.barangayCode);
  const cleanedValues = cleanAddNewDataValues(values);
  const householdQuestions = cleanedValues.householdQuestions;
  const formattedRbimDisplayCodes = [
    rbimDisplayCodes.provinceOrHucCode,
    rbimDisplayCodes.municipalityCityCode,
    rbimDisplayCodes.barangayCode,
  ]
    .filter(Boolean)
    .join(" | ");

  return (
    <ScrollableFormShell
      contentClassName="space-y-4 bg-white p-3 sm:p-4"
      minWidthClassName="min-w-0"
      scrollerClassName="overflow-x-hidden"
    >
      <Card>
        <CardHeader>
          <CardTitle>Review and Submit</CardTitle>
          <CardDescription>
            Review the cleaned household details and member information before submitting.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
          <div className="min-w-0 rounded-xl border bg-background p-4">
            <h3 className="text-sm font-semibold">Household Information</h3>
            <dl className="mt-3 grid gap-2 text-sm">
              <SummaryItem label="Province" value={values.household.province} />
              <SummaryItem label="Municipality" value={values.household.municipality} />
              <SummaryItem label="Barangay" value={values.household.barangay} />
              <SummaryItem label="RBIM Codes" value={formattedRbimDisplayCodes} />
              <SummaryItem label="Address" value={values.household.address} />
              <SummaryItem label="Household Code" value={values.household.householdCode} />
              <SummaryItem label="Family" value={values.household.family || "A"} />
              {values.household.family !== "A" ? (
                <SummaryItem
                  label={`Relationship of Family ${values.household.family} to Household Head`}
                  value={values.household.familyRelationshipToHouseholdHead || "Missing"}
                />
              ) : null}
              <SummaryItem label="Household Type" value={values.household.householdType} />
              <SummaryItem label="Respondent" value={values.household.respondentName} />
              <SummaryItem label="Household Head" value={values.household.householdHead} />
              <SummaryItem label="Total Members" value={values.household.totalMembers} />
            </dl>
          </div>

          <div className="min-w-0 max-w-full overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[2860px] text-left text-sm">
              <thead className="bg-emerald-50 text-xs uppercase text-emerald-900">
                <tr>
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Sex</th>
                  <th className="px-4 py-3 font-semibold">DOB</th>
                  <th className="px-4 py-3 font-semibold">Age</th>
                  <th className="px-4 py-3 font-semibold">Place of Birth</th>
                  <th className="px-4 py-3 font-semibold">Relationship</th>
                  <th className="px-4 py-3 font-semibold">Civil Status</th>
                  <th className="px-4 py-3 font-semibold">Nationality</th>
                  <th className="px-4 py-3 font-semibold">Religion</th>
                  <th className="px-4 py-3 font-semibold">Ethnicity</th>
                  <th className="px-4 py-3 font-semibold">Education</th>
                  <th className="px-4 py-3 font-semibold">Enrolled</th>
                  <th className="px-4 py-3 font-semibold">School Level</th>
                  <th className="px-4 py-3 font-semibold">Place of School</th>
                  <th className="px-4 py-3 font-semibold">Reason Not Attending</th>
                  <th className="px-4 py-3 font-semibold">Income</th>
                  <th className="px-4 py-3 font-semibold">Source</th>
                  <th className="px-4 py-3 font-semibold">Work Status</th>
                  <th className="px-4 py-3 font-semibold">Work Place</th>
                  <th className="px-4 py-3 font-semibold">Delivery</th>
                  <th className="px-4 py-3 font-semibold">Attendant</th>
                  <th className="px-4 py-3 font-semibold">Immunization</th>
                  <th className="px-4 py-3 font-semibold">Living Children</th>
                  <th className="px-4 py-3 font-semibold">FP Source</th>
                  <th className="px-4 py-3 font-semibold">FP Intention</th>
                  <th className="px-4 py-3 font-semibold">Health Insurance</th>
                  <th className="px-4 py-3 font-semibold">Facility / Reason</th>
                  <th className="px-4 py-3 font-semibold">Disability</th>
                  <th className="px-4 py-3 font-semibold">Participation</th>
                  <th className="px-4 py-3 font-semibold">Resident Type</th>
                  <th className="px-4 py-3 font-semibold">Q34-Q36 Residence History</th>
                  <th className="px-4 py-3 font-semibold">Migration</th>
                  <th className="px-4 py-3 font-semibold">Reasons</th>
                  <th className="px-4 py-3 font-semibold">CTC</th>
                  <th className="px-4 py-3 font-semibold">Skills</th>
                  <th className="px-4 py-3 font-semibold">FP Use</th>
                </tr>
              </thead>
              <tbody className="divide-y bg-card">
                {cleanedValues.members.map((member, index) => {
                  const age = formatAge(member);
                  const name = [member.firstName, member.middleName, member.lastName]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <tr key={`${member.firstName}-${member.lastName}-${index}`}>
                      <td className="px-4 py-3 font-medium">{name || "Incomplete name"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.sex} /></td>
                      <td className="px-4 py-3">{member.displayBirthdate || member.dateOfBirth || "Missing"}</td>
                      <td className="px-4 py-3">{age}</td>
                      <td className="px-4 py-3">{member.placeOfBirth || "Missing"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.relationshipToHead} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.civilStatus} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.nationality} /></td>
                      <td className="px-4 py-3">{member.religion || "Missing"}</td>
                      <td className="px-4 py-3">{member.ethnicity || "Missing"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.highestEducation} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.currentlyEnrolled} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.schoolLevel} /></td>
                      <td className="px-4 py-3">{member.placeOfSchool || "Missing"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.reasonNotAttendingSchool} /></td>
                      <td className="px-4 py-3">{member.monthlyIncome || "Missing"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.sourceOfIncome} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.workStatus} /></td>
                      <td className="px-4 py-3">{member.placeOfWork || "Missing"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.placeOfDelivery} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.birthAttendant} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.immunization} /></td>
                      <td className="px-4 py-3">{member.livingChildren || "Missing"}</td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.fpMethodSource} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.intentionToUseFp} /></td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.healthInsurance} /></td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.facilityVisitedPast12Months} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.healthFacilityVisitReason} />
                        </div>
                      </td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.disability} /></td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.soloParentStatus} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.registeredSeniorCitizen} />
                        </div>
                        <div className="mt-1">{member.registeredVoterBarangay || "Missing"}</div>
                      </td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.residentType} /></td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.lengthOfStayInBarangay} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.previousResidenceFiveYears} />
                        </div>
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.previousResidenceSixMonths} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.dateOfTransfer} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.returnToPreviousResidence} />
                        </div>
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.durationOfStayCurrentBarangay} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.reasonLeavingPreviousResidence1} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.reasonLeavingPreviousResidence2} />
                        </div>
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.reasonLeavingPreviousResidence3} />
                        </div>
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.transferReason1} />
                        </div>
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.transferReason2} />
                        </div>
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.transferReason3} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.hasValidCtc} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.ctcIssuedInBarangay} />
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <CodeDisplay result={member.codedFields.skillsTrainingInterest} />
                        <div className="mt-1">
                          <CodeDisplay result={member.codedFields.skills} />
                        </div>
                      </td>
                      <td className="px-4 py-3"><CodeDisplay result={member.codedFields.familyPlanningUse} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Interview and Encoding Information</CardTitle>
          <CardDescription>Front-page tracking details from the RBIM census form.</CardDescription>
        </CardHeader>
        <CardContent className="grid min-w-0 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <div className="min-w-0 max-w-full overflow-x-auto rounded-xl border">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="bg-emerald-50 text-xs uppercase text-emerald-900">
                <tr>
                  <th className="px-4 py-3 font-semibold">Visit</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-4 py-3 font-semibold">Time</th>
                  <th className="px-4 py-3 font-semibold">Result</th>
                  <th className="px-4 py-3 font-semibold">Next Visit</th>
                  <th className="px-4 py-3 font-semibold">Interviewer</th>
                  <th className="px-4 py-3 font-semibold">Supervisor</th>
                </tr>
              </thead>
              <tbody className="divide-y bg-card">
                {values.interviews.map((interview) => (
                  <tr key={interview.visit}>
                    <td className="px-4 py-3 font-medium">{interview.visit}</td>
                    <td className="px-4 py-3">{interview.dateOfVisit || "Missing"}</td>
                    <td className="px-4 py-3">
                      {[interview.timeStart, interview.timeEnd].filter(Boolean).join(" - ") || "Missing"}
                    </td>
                    <td className="px-4 py-3">{interview.result || "Missing"}</td>
                    <td className="px-4 py-3">{interview.dateOfNextVisit || "Missing"}</td>
                    <td className="px-4 py-3">{interview.interviewer || "Missing"}</td>
                    <td className="px-4 py-3">{interview.supervisor || "Missing"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="min-w-0 rounded-xl border bg-background p-4">
            <h3 className="text-sm font-semibold">Encoding Information</h3>
            <dl className="mt-3 grid gap-2 text-sm">
              <SummaryItem label="Date Encoded" value={values.encoding.dateEncoded} />
              <SummaryItem label="Encoder" value={values.encoding.encoder} />
              <SummaryItem label="Supervisor" value={values.encoding.supervisor} />
            </dl>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Household Questions</CardTitle>
          <CardDescription>Pages 10 - 11 household-level RBIM responses.</CardDescription>
        </CardHeader>
        <CardContent className="grid min-w-0 gap-4 xl:grid-cols-2">
          <div className="min-w-0 rounded-xl border bg-background p-4">
            <h3 className="text-sm font-semibold">Housing and Utilities</h3>
            <dl className="mt-3 grid gap-2 text-sm">
              <SummaryCodeItem label="Housing Tenure" result={householdQuestions.codedFields.housingTenure} />
              <SummaryCodeItem label="Lot Tenure" result={householdQuestions.codedFields.lotTenure} />
              <SummaryCodeItem label="Hazard-Prone Area" result={householdQuestions.codedFields.hazardProneArea} />
              <SummaryCodeItem
                label="Disaster Past 5 Years"
                result={householdQuestions.codedFields.disasterExperiencedPastFiveYears}
              />
              <SummaryCodeItem
                label="Nearest Evacuation Center"
                result={householdQuestions.codedFields.knowsNearestEvacuationCenter}
              />
              <SummaryCodeItem label="Lighting Fuel" result={householdQuestions.codedFields.lightingFuel} />
              <SummaryCodeItem label="Cooking Fuel" result={householdQuestions.codedFields.cookingFuel} />
              <SummaryCodeItem label="Drinking Water" result={householdQuestions.codedFields.drinkingWaterSource} />
              <SummaryCodeItem label="Garbage" result={householdQuestions.codedFields.garbageDisposal} />
              <SummaryCodeItem label="Segregates" result={householdQuestions.codedFields.segregatesGarbage} />
              <SummaryCodeItem label="Toilet" result={householdQuestions.codedFields.toiletFacility} />
              <SummaryCodeItem label="Building Type" result={householdQuestions.codedFields.buildingType} />
              <SummaryCodeItem label="Wall Material" result={householdQuestions.codedFields.wallMaterial} />
            </dl>
          </div>

          <div className="min-w-0 rounded-xl border bg-background p-4">
            <h3 className="text-sm font-semibold">Deaths, Needs, and Future Residence</h3>
            <dl className="mt-3 grid gap-2 text-sm">
              <SummaryCodeItem
                label="Female Death"
                result={householdQuestions.codedFields.femaleDeathPast12Months}
              />
              <SummaryItem
                label="Female Details"
                value={formatJoined([householdQuestions.femaleDeathAge, householdQuestions.femaleDeathCause])}
              />
              <SummaryCodeItem
                label="Child Death"
                result={householdQuestions.codedFields.childDeathPast12Months}
              />
              <SummaryCodeItem label="Child Sex" result={householdQuestions.codedFields.childDeathSex} />
              <SummaryItem
                label="Child Details"
                value={formatJoined([householdQuestions.childDeathAge, householdQuestions.childDeathCause])}
              />
              <SummaryItem
                label="Common Diseases"
                value={formatJoined([
                  householdQuestions.commonDiseaseCause1,
                  householdQuestions.commonDiseaseCause2,
                  householdQuestions.commonDiseaseCause3,
                ])}
              />
              <SummaryItem
                label="Primary Needs"
                value={formatJoined([
                  householdQuestions.primaryNeed1,
                  householdQuestions.primaryNeed2,
                  householdQuestions.primaryNeed3,
                ])}
              />
              <SummaryItem
                label="In 5 Years"
                value={formatJoined([
                  householdQuestions.futureBarangay,
                  householdQuestions.futureMunicipality,
                  householdQuestions.futureProvince,
                ])}
              />
            </dl>
          </div>
        </CardContent>
      </Card>
    </ScrollableFormShell>
  );
}


function SummaryItem({ label, value }: { label: string; value?: string }) {
  return (
    <div className="grid min-w-0 grid-cols-[minmax(100px,150px)_minmax(0,1fr)] gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium">{value || "Missing"}</dd>
    </div>
  );
}

function SummaryCodeItem({
  label,
  result,
}: {
  label: string;
  result?: { code: string; label: string | null; error?: string };
}) {
  return (
    <div className="grid min-w-0 grid-cols-[minmax(100px,150px)_minmax(0,1fr)] gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words font-medium">
        <CodeDisplay result={result} />
      </dd>
    </div>
  );
}

function CodeDisplay({ result }: { result?: { code: string; label: string | null; error?: string } }) {
  if (!result?.code && !result?.label) {
    return <span className="text-muted-foreground">Missing</span>;
  }

  if (result.error) {
    return (
      <span className="text-destructive">
        <span className="block font-semibold">Invalid code</span>
        <span className="block text-xs">Encoded Code: {result.code}</span>
      </span>
    );
  }

  return (
    <span>
      <span className="block font-semibold">{result.label ?? "Missing"}</span>
    </span>
  );
}

function formatJoined(values: Array<string | undefined>) {
  return values.filter(Boolean).join(" / ") || "NONE";
}

function formatAge(member: BirthdateResolverInput) {
  const resolvedAge = resolveMemberAge(member);
  const ageDisplay = formatOfficialAgeDisplay(resolvedAge);

  if (!ageDisplay) {
    return "";
  }

  if (resolvedAge.ageBasis === "exact") {
    return `${ageDisplay} — ${getAgeSourceLabel(resolvedAge)}`;
  }

  return ageDisplay;
}
